import React, { type FC, useCallback } from 'react';
import { Formik, Form, type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type UiHStackProps,
  UiStack,
  UiButton,
  UiHStack, UiIconCaretRight, UiIconCaretLeft,
} from '@/lib/ui';
import BaseTransitionSlideLeft from '@/base/Transition/SlideLeft';
import LayoutFooter from '@/registration/component/Register/Layout/Footer';
import BaseFormCheckboxListField, { type CheckboxOption } from '@/base/Form/CheckboxListField';
import BaseFormFieldGroup from '@/base/Form/FieldGroup';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type DiscountCode, discountCodeAnyQueryKey, checkAnyDiscounts, cartQueryKey, AttendeeCategoryData } from '@/api/registration';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import DiscountCodeForm from './DiscountCodeForm';
import { type ApiResponse, type ApiResponseSingle } from '@/api/tenantClient';
import { registration as registrationAPI } from '@/api';
import { useUserSession } from '@/registration/hook/useUserSession';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';
import { type GroupRegistration, GroupRegistrationStatus, type GroupRegistrationUpdateRequest, groupRegistrationQueryKey, updateGroupRegistration } from '@/api/registration/groupRegistration';
import { useRegisterGroupSteps } from '@/registration/hook/useRegisterGroupSteps';
import PolicyCheckBox from '@/registration/component/Register/PolicyCheckBox';
import BaseThemeButton from '@/base/ThemeButton';
import BrandFooter from '@/registration/component/Register/Layout/BrandFooter';

export interface CategoryFormProps extends UiHStackProps {
  attendeeCategoryId: number | undefined
  attendeeCategoryData: AttendeeCategoryData[] | undefined
}

export interface FormData {
  attendeeCategory: string | undefined
  discountCodeString?: string
  discountCode?: DiscountCode
  isPrivacyPolicyAgreed: boolean
  isTermsAndConditionsAgreed: boolean
  // strict value
  privacyPolicyUrl?: string
  termsAndConditionsUrl?: string
}

const initFormData: FormData = {
  attendeeCategory: '',
  isPrivacyPolicyAgreed: false,
  isTermsAndConditionsAgreed: false
};

const formSchema = Yup.object().shape({
  privacyPolicyUrl: Yup.string().nullable(),
  termsAndConditionsUrl: Yup.string().nullable(),
  discountCodeString: Yup.string().nullable(),
  discountCode: Yup.object({
    id: Yup.string(),
    discountCode: Yup.string()
  }).nullable(),
  attendeeCategory: Yup.string().required('Please choose an attendee group'),
  isPrivacyPolicyAgreed: Yup.boolean().when('privacyPolicyUrl', {
    is: (value: string) => { return (!!value); },
    then: (schema) => { return schema.isTrue('Please agree with the privacy policy before you continue'); }
  }),
  isTermsAndConditionsAgreed: Yup.boolean().when('termsAndConditionsUrl', {
    is: (value: string) => { return (!!value); },
    then: (schema) => { return schema.isTrue('Please agree with terms and condition before you continue'); }
  }),
});

const CategoryForm: FC<CategoryFormProps> = ({
  attendeeCategoryId,
  attendeeCategoryData
}) => {
  const queryClient = useQueryClient();
  const { eventId } = useRegisterRoute();
  const { createTenantApiRequest } = useTenantApi();
  const eventSettingQuery = useEventSettingsQuery(eventId);
  const { userSession } = useUserSession();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const { toNextStepWithSteps } = useRegisterGroupSteps();

  const options: CheckboxOption[] = (Array.isArray(attendeeCategoryData) ? attendeeCategoryData :  []).filter((attendeeCategory) => {
    if (attendeeCategoryId) {
      return Number(attendeeCategoryId) === attendeeCategory.id;
    } else {
      return attendeeCategory.isVisible;
    }
  }).map((attendeeCategory) => {
    return {
      value: attendeeCategory.id.toString(),
      label: attendeeCategory.name,
      description: attendeeCategory.description,
    };
  });

  const anyDiscountsQuery = useQuery<boolean, Error>({
    queryKey: [discountCodeAnyQueryKey, { eventId }],
    queryFn: async () => {
      const result = await checkAnyDiscounts(createTenantApiRequest)({ eventId });
      return result.item.any;
    }
  });

  const { mutateAsync: updateGroupRegistrationMutate, isLoading: isUpdateGroupRegistrationLoading, error } = useMutation<ApiResponseSingle<GroupRegistration>, Error, GroupRegistrationUpdateRequest>({
    mutationFn: async (data: GroupRegistrationUpdateRequest) => {
      return await updateGroupRegistration(createTenantApiRequest)(data);
    },
    onSuccess: async (data: ApiResponseSingle<GroupRegistration>) => {
      if (data.errors && data.errors.length > 0) {
        throw new Error(data.errors.join(', '));
      }
      await queryClient.invalidateQueries({ queryKey: [groupRegistrationQueryKey, { eventId }, { token: userSession?.authToken }] });
      toNextStepWithSteps(data.item.groupRegistrationSteps);
    },
    onError: (registrationError: any) => {
      throw registrationError;
    }
  });

  const { mutateAsync: checkDiscountCode, isLoading: isCheckingDiscountCode } = useMutation<ApiResponse<DiscountCode>, Error, string>({
    mutationFn: async (code: string) => {
      return await registrationAPI.checkDiscountCode(createTenantApiRequest)({ eventId, discountCode: code });
    }
  });

  const onSubmit = useCallback(
    async (
      values: FormData,
      { setSubmitting }: FormikHelpers<FormData>
    ) => {
      setSubmitting(true);
      if (!groupRegistrationQuery.data) {
        throw new Error('Registration not found');
      }
      const data = updateGroupRegistrationMutate({
        groupRegistration: {
          id: groupRegistrationQuery.data.id,
          attendeeCategoryId: Number(values.attendeeCategory),
          termsAndConditionsAccepted: values.isTermsAndConditionsAgreed,
          privacyPolicyAccepted: values.isPrivacyPolicyAgreed,
          ...values.discountCode ? { discountId: values.discountCode.id } : {}
        }
      });
      setSubmitting(false);
      // Reload cart - check added item available for the selected attendee category or not
      const groupRegistrationId = groupRegistrationQuery.data.id;
      void queryClient.invalidateQueries({ queryKey: [cartQueryKey, { groupRegistrationId }] });
      toNextStepWithSteps((await data).item.groupRegistrationSteps);
    }, [groupRegistrationQuery.data, updateGroupRegistrationMutate]);

  return (
    <QueryContainer query={eventSettingQuery}>
      {(eventSetting) => {
        return (
          <QueryContainer query={anyDiscountsQuery}>
            {(anyDiscounts) => {
              return (
                <Formik<FormData>
                  initialValues={{
                    ...initFormData,
                    privacyPolicyUrl: eventSetting.privacy?.privacyPolicyUrl,
                    termsAndConditionsUrl: eventSetting.privacy?.termsAndConditionsUrl,
                    attendeeCategory: attendeeCategoryId ? attendeeCategoryId.toString() : (groupRegistrationQuery?.data?.attendeeCategoryId ? groupRegistrationQuery?.data?.attendeeCategoryId.toString() : undefined),
                    discountCodeString: groupRegistrationQuery?.data?.discount?.discountCode ?? undefined,
                    discountCode: groupRegistrationQuery?.data?.discount ?? undefined,
                    isPrivacyPolicyAgreed: groupRegistrationQuery?.data?.privacyPolicyAccepted ?? false,
                    isTermsAndConditionsAgreed: groupRegistrationQuery?.data?.termsAndConditionsAccepted ?? false
                  }}
                  validateOnChange={false}
                  validateOnBlur={false}
                  validationSchema={formSchema}
                  onSubmit={onSubmit}
                  enableReinitialize
                >
                  <Form style={{ display: 'flex', flexDirection: 'column' }}>
                    <BaseTransitionSlideLeft>
                      <BaseFormFieldGroup>
                        {error && (
                          <UiStack spacing={4} flexGrow={1} py={4}>
                            <BaseMessageBarError>
                              {error.message ?? 'Failed to save the host.'}
                            </BaseMessageBarError>
                          </UiStack>
                        )}
                        {options.length > 0 && (
                          <BaseFormCheckboxListField
                            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                            label={eventSetting?.pageMetadata?.attendeeCategory?.title || 'Attendee group'}
                            name={'attendeeCategory'}
                            options={options}
                            isMultiple={false}
                            layout={'stack'}
                            disabled={(!!userSession?.authToken && groupRegistrationQuery?.data?.status === GroupRegistrationStatus.COMPLETED) ?? false}
                          />
                        )}
                        {anyDiscounts && (
                                <DiscountCodeForm
                                  existingDiscountCode={groupRegistrationQuery?.data?.discount}
                                  onCheckDiscountCode={checkDiscountCode}
                                />
                              )}
                        <BaseDividerHorizontal height={1} />
                        <UiStack spacing={4} pb={4}>
                          {!!eventSetting?.privacy.privacyPolicyUrl && (
                            <PolicyCheckBox checkboxName='isPrivacyPolicyAgreed' policyText={'Privacy Policy'} policyUrl={eventSetting.privacy.privacyPolicyUrl} />
                          )}
                          {!!eventSetting?.privacy.termsAndConditionsUrl && (
                            <PolicyCheckBox checkboxName='isTermsAndConditionsAgreed' policyText={'Terms and Conditions'} policyUrl={eventSetting.privacy.termsAndConditionsUrl} />
                          )}
                        </UiStack>
                      </BaseFormFieldGroup>
                    </BaseTransitionSlideLeft>
                    <UiStack height={96} />
                    <LayoutFooter isGroup>
                      <UiHStack justifyContent={'flex-end'} flexGrow={1}>
                        {/*<UiButton*/}
                        {/*  px={8}*/}
                        {/*  size={'lg'}*/}
                        {/*  colorScheme={'primary'}*/}
                        {/*  type={'submit'}*/}
                        {/*  isLoading={isCheckingDiscountCode || isUpdateGroupRegistrationLoading}*/}
                        {/*  variant={'ghost'}*/}
                        {/*  fontSize={'lg'}*/}
                        {/*  rightIcon={(*/}
                        {/*    <UiIconCaretRight color={'primary.800'} size={'xl'} weight={'bold'}/>*/}
                        {/*  )}*/}
                        {/*>*/}
                        {/*  Next*/}
                        {/*</UiButton>*/}

                        <UiStack width={24}/>
                        <BrandFooter/>
                        <BaseThemeButton
                          theme={'primary'}
                          type={'submit'}
                          isLoading={isCheckingDiscountCode || isUpdateGroupRegistrationLoading}
                          rightIcon={(
                            <UiIconCaretRight color={'primary.800'} size={'lg'} weight={'bold'}/>
                          )}
                        >
                          Next
                        </BaseThemeButton>
                      </UiHStack>
                    </LayoutFooter>
                  </Form>
                </Formik>
              );
            }}
          </QueryContainer>
        );
      }}
    </QueryContainer>
  );
};

export default CategoryForm;
